// apiConfig.js - Configuration for API services
// AI API calls are now proxied through the backend — no SDK clients needed here.

//------------------------------------------------
// BACKEND API BASE URL
//------------------------------------------------
import { API_BASE_URL } from './backendClient';

export const AI_API_BASE = `${API_BASE_URL}/api/ai`;

//------------------------------------------------
// NON-AI KEYS (still needed in frontend)
//------------------------------------------------
export const TYPESENSE_API_KEY = process.env.REACT_APP_TYPESENSE_API_KEY;
export const TYPESENSE_HOST = process.env.REACT_APP_TYPESENSE_HOST || "https://ts.jet-code.com";
export const GROCERY_HOST = process.env.REACT_APP_GROCERY_HOST || "https://grocery.9meals.ai";

console.log('Environment variables check:', {
  AI_API_BASE: AI_API_BASE,
  TYPESENSE_API_KEY: TYPESENSE_API_KEY ? '✓ Available' : '✗ Missing',
  TYPESENSE_HOST: TYPESENSE_HOST ? '✓ Available' : '✗ Missing',
  GROCERY_HOST: GROCERY_HOST ? '✓ Available' : '✗ Missing',
});

//------------------------------------------------
// MODEL CONFIGURATION
//------------------------------------------------

/**
 * Flat model configuration — set the model name for each task,
 * the provider is auto-detected from the model name via getProviderForModel().
 * 
 * To switch a task to a different provider, just change the model name here.
 * Supported prefixes: 'gpt-' / 'o3' → openai, 'claude-' → anthropic,
 * 'deepseek-' → deepseek, 'gemini-' → gemini.
 */
// Recipe details and alternative suggestions intentionally use the SAME model —
// change it here and both stay in lockstep.
const DETAILS_MODEL = 'gpt-5.6-luna';

export const MODEL_CONFIG = {
  completion:  'gpt-5.6-terra',      // Meal plan generation
  details:     DETAILS_MODEL,        // Recipe details
  ingredients: 'gpt-5-nano',         // Ingredient extraction (Typesense synonym generation)
  grocery:     'gemini-2.5-flash-lite', // Grocery list generation (Gemini)
  groceryFallback: 'gpt-5-mini',     // AI grocery fallback (unmatched ingredients)
  alternative: DETAILS_MODEL,        // Alternative recipe suggestions — same model as details
};

/**
 * Detect the provider from a model name string.
 * @param {string} model - Model identifier (e.g. 'gpt-5-mini', 'claude-haiku-4-5')
 * @returns {'openai'|'anthropic'|'deepseek'|'gemini'} Provider key
 */
export const getProviderForModel = (model) => {
  if (!model) return 'openai';
  if (model.startsWith('claude-')) return 'anthropic';
  if (model.startsWith('deepseek-')) return 'deepseek';
  if (model.startsWith('gemini-')) return 'gemini';
  // Default: OpenAI (covers gpt-*, o3*, etc.)
  return 'openai';
};

export const MODEL_REASONING_CONFIG = {
  completion: {
    effort: 'low',
    maxTokens: 6000,
  },
  // Recipe details is structured JSON from a fully-specified prompt — default
  // (medium) reasoning burns hidden output tokens ×9 calls per generation.
  // Requires the backend /recipe-details route to forward `reasoning`.
  details: {
    effort: 'low',
  },
  // Alternatives: same rationale as details (structured JSON, cost-sensitive).
  alternative: {
    effort: 'low',
  },
};

export const supportsOpenAIReasoning = (model) => {
  if (!model) return false;
  return /^o\d/i.test(model) || /^gpt-5(?:[.-]|$)/i.test(model);
};

// Legacy export — some code may still reference ACTIVE_PROVIDER for logging.
// Derived from the completion model for backward compatibility.
export const ACTIVE_PROVIDER = getProviderForModel(MODEL_CONFIG.completion);

//------------------------------------------------
// MODEL PRICING (per 1M tokens, USD)
//------------------------------------------------

/**
 * Pricing table for OpenAI models.
 * Prices are per 1,000,000 tokens in USD.
 * Update this table when switching models or when pricing changes.
 * Source: https://platform.openai.com/docs/pricing
 */
export const MODEL_PRICING = {
  // OpenAI models
  // gpt-5.6 family — short-context rates (our prompts are 2–7K tokens, far below the
  // long-context threshold where input doubles and output rises ×1.5)
  'gpt-5.6-sol':   { input: 5.00,  cachedInput: 0.50,  output: 30.00 },
  'gpt-5.6-terra': { input: 2.50,  cachedInput: 0.25,  output: 15.00 },
  'gpt-5.6-luna':  { input: 1.00,  cachedInput: 0.10,  output: 6.00  },
  'gpt-5.4':       { input: 2.50,  cachedInput: 0.25,  output: 15.00 },
  'gpt-5.4-mini':  { input: 0.75,  cachedInput: 0.075, output: 4.50  },
  'gpt-5.4-nano':  { input: 0.20,  cachedInput: 0.02,  output: 1.25  },
  'gpt-5.2':       { input: 1.75,  cachedInput: 0.175, output: 14.00 },
  'gpt-5.1':       { input: 1.25,  cachedInput: 0.125, output: 10.00 },
  'gpt-5':         { input: 1.25,  cachedInput: 0.125, output: 10.00 },
  'gpt-4.1-mini':  { input: 0.40,  cachedInput: 0.10,  output: 1.60  },
  'gpt-5-mini':    { input: 0.25,  cachedInput: 0.025, output: 2.00  },
  'gpt-5-nano':    { input: 0.10,  cachedInput: 0.025, output: 0.40  },
  'o3-mini':       { input: 1.10,  cachedInput: 0.55,  output: 4.40  },
  'o3':            { input: 2.00,  cachedInput: 0.50,  output: 8.00  },
  // Anthropic models
  'claude-opus-4-6':    { input: 5.00,  cachedInput: 0.50,  output: 25.00 },
  'claude-opus-4-5':    { input: 5.00,  cachedInput: 0.50,  output: 25.00 },
  'claude-sonnet-4-5':          { input: 3.00,  cachedInput: 0.30,  output: 15.00 },
  'claude-sonnet-4':    { input: 3.00,  cachedInput: 0.30,  output: 15.00 },
  'claude-haiku-4-5':          { input: 1.00,  cachedInput: 0.10,  output: 5.00  },
  'claude-haiku-4-5-20251001': { input: 1.00,  cachedInput: 0.10,  output: 5.00  },
};

/**
 * Runware image generation pricing.
 * Cost per image for the rundiffusion:210@100 model at 960×512.
 * Runware returns exact cost via `includeCost: true` — this is an estimate.
 */
export const RUNWARE_PRICING = {
  costPerImage: 0.0036, // ~$0.0036 per 960×512 image (25 steps)
};

/**
 * Typical token usage per API call (estimated from real usage).
 * Used for cost estimation — actual usage may vary.
 */
export const TYPICAL_TOKEN_USAGE = {
  /** Meal plan generation (MODEL_CONFIG.completion): 1 call per generation */
  completion: { inputTokens: 4500, outputTokens: 5500 },
  /** Recipe details (MODEL_CONFIG.details): 9 calls per generation (1 per recipe) */
  details:    { inputTokens: 1800, outputTokens: 1800 },
  /** Alternative recipes (MODEL_CONFIG.alternative): on-demand, 1 call per replacement */
  alternative:{ inputTokens: 1200, outputTokens: 1200 },
  /** Ingredient extraction (gpt-5-nano): used in grocery processing */
  ingredients:{ inputTokens: 800,  outputTokens: 400  },
};

/**
 * Calculate cost in USD for a given model and token count.
 * @param {string} model - Model name (e.g., 'gpt-5.1')
 * @param {number} inputTokens - Number of input tokens
 * @param {number} outputTokens - Number of output tokens
 * @param {boolean} cached - Whether input tokens are cached (cheaper)
 * @returns {number} Cost in USD
 */
export const calculateModelCost = (model, inputTokens, outputTokens, cached = false) => {
  const pricing = MODEL_PRICING[model];
  if (!pricing) {
    console.warn(`[Pricing] No pricing data for model: ${model}`);
    return 0;
  }
  const inputRate = cached ? pricing.cachedInput : pricing.input;
  return (inputTokens / 1_000_000) * inputRate + (outputTokens / 1_000_000) * pricing.output;
};

/**
 * Tracks actual API costs during a generation session.
 * Reset at start of each generation, accumulates real costs from usage data.
 */
export const generationCostTracker = {
  _completion: null,
  _completionAttempts: [],
  _recipeDetails: [],
  _images: [],

  reset() {
    this._completion = null;
    this._completionAttempts = [];
    this._recipeDetails = [];
    this._images = [];
    console.log('%c[💰 Cost Tracker] Reset — new generation session', 'color: #ff9800; font-weight: bold');
  },

  /** Log meal plan completion cost (accumulates across structure-retry attempts) */
  logCompletion(model, inputTokens, outputTokens, cachedTokens = 0) {
    const cost = calculateModelCost(model, inputTokens, outputTokens);
    const cachedSavings = cachedTokens > 0
      ? ((cachedTokens / 1_000_000) * ((MODEL_PRICING[model]?.input || 0) - (MODEL_PRICING[model]?.cachedInput || 0)))
      : 0;
    const effectiveCost = cost - cachedSavings;
    const entry = { model, inputTokens, outputTokens, cachedTokens, cost: effectiveCost };
    this._completionAttempts.push(entry);
    this._completion = entry; // latest attempt (kept for backward-compat reads)
    const attemptIdx = this._completionAttempts.length;
    console.log(
      `%c[💰 Step 1: Meal Plan${attemptIdx > 1 ? ` (attempt ${attemptIdx})` : ''}] ${model}%c — ${inputTokens.toLocaleString()} in / ${outputTokens.toLocaleString()} out${cachedTokens ? ` (${cachedTokens.toLocaleString()} cached)` : ''} → $${effectiveCost.toFixed(6)}`,
      'color: #4caf50; font-weight: bold', 'color: #4caf50'
    );
  },

  /** Log single recipe details cost */
  logRecipeDetails(title, model, inputTokens, outputTokens, cachedTokens = 0) {
    const cost = calculateModelCost(model, inputTokens, outputTokens);
    const cachedSavings = cachedTokens > 0
      ? ((cachedTokens / 1_000_000) * ((MODEL_PRICING[model]?.input || 0) - (MODEL_PRICING[model]?.cachedInput || 0)))
      : 0;
    const effectiveCost = cost - cachedSavings;
    this._recipeDetails.push({ title, model, inputTokens, outputTokens, cachedTokens, cost: effectiveCost });
    const idx = this._recipeDetails.length;
    console.log(
      `%c[💰 Step 2: Recipe ${idx}/9] "${title}"%c — ${model} — ${inputTokens.toLocaleString()} in / ${outputTokens.toLocaleString()} out${cachedTokens ? ` (${cachedTokens.toLocaleString()} cached)` : ''} → $${effectiveCost.toFixed(6)}`,
      'color: #2196f3; font-weight: bold', 'color: #2196f3'
    );
  },

  /** Log image generation cost */
  logImage(title, provider, cost) {
    this._images.push({ title, provider, cost });
    const idx = this._images.length;
    console.log(
      `%c[💰 Step 3: Image ${idx}/9] "${title}"%c — ${provider} → $${cost?.toFixed(6) || 'N/A'}`,
      'color: #9c27b0; font-weight: bold', 'color: #9c27b0'
    );
  },

  /** Print full cost summary */
  printSummary() {
    const attempts = this._completionAttempts.length || (this._completion ? 1 : 0);
    const compCost = this._completionAttempts.length > 0
      ? this._completionAttempts.reduce((s, a) => s + a.cost, 0)
      : (this._completion?.cost || 0);
    const detailsCost = this._recipeDetails.reduce((s, r) => s + r.cost, 0);
    const imagesCost = this._images.reduce((s, r) => s + (r.cost || 0), 0);
    const total = compCost + detailsCost + imagesCost;

    console.log('%c\n════════════════════════════════════════', 'color: #ff9800');
    console.log('%c  💰 GENERATION COST SUMMARY', 'color: #ff9800; font-weight: bold; font-size: 14px');
    console.log('%c════════════════════════════════════════', 'color: #ff9800');
    const attemptsLabel = attempts > 1 ? ` ×${attempts} attempts` : '';
    console.log(`%c  Step 1 — Meal Plan (${this._completion?.model || '?'})${attemptsLabel}:  $${compCost.toFixed(6)}`, 'color: #4caf50');
    console.log(`%c  Step 2 — Recipe Details ×${this._recipeDetails.length}:          $${detailsCost.toFixed(6)}`, 'color: #2196f3');
    if (this._recipeDetails.length > 0) {
      console.log(`%c           (avg per recipe: $${(detailsCost / this._recipeDetails.length).toFixed(6)})`, 'color: #2196f3');
    }
    console.log(`%c  Step 3 — Images ×${this._images.length}:                  $${imagesCost.toFixed(6)}`, 'color: #9c27b0');
    if (this._images.length > 0) {
      console.log(`%c           (avg per image: $${(imagesCost / this._images.length).toFixed(6)})`, 'color: #9c27b0');
    }
    console.log('%c────────────────────────────────────────', 'color: #ff9800');
    console.log(`%c  TOTAL: $${total.toFixed(6)}`, 'color: #ff9800; font-weight: bold; font-size: 13px');
    console.log('%c════════════════════════════════════════\n', 'color: #ff9800');
  },
};

// Expose tracker globally for console access: __COST_TRACKER.printSummary()
if (typeof window !== 'undefined') {
  window.__COST_TRACKER = generationCostTracker;
}

/**
 * Estimate total cost for a full meal plan generation cycle.
 * Includes: 1× completion + 9× recipe details + 9× images.
 * Does NOT include Gemini grocery (free tier / separate billing) or on-demand alternatives.
 * 
 * @param {Object} [modelOverrides] - Override specific models for estimation
 * @returns {Object} Breakdown: { completion, recipeDetails, images, total, perGeneration }
 */
export const estimateGenerationCost = (modelOverrides = {}) => {
  const models = {
    completion: modelOverrides.completion || MODEL_CONFIG.completion,
    details: modelOverrides.details || MODEL_CONFIG.details,
  };

  const completionCost = calculateModelCost(
    models.completion,
    TYPICAL_TOKEN_USAGE.completion.inputTokens,
    TYPICAL_TOKEN_USAGE.completion.outputTokens
  );

  const singleDetailCost = calculateModelCost(
    models.details,
    TYPICAL_TOKEN_USAGE.details.inputTokens,
    TYPICAL_TOKEN_USAGE.details.outputTokens
  );
  const recipeDetailsCost = singleDetailCost * 9; // 9 recipes

  const imagesCost = RUNWARE_PRICING.costPerImage * 9; // 9 images

  const total = completionCost + recipeDetailsCost + imagesCost;

  return {
    completion:    { model: models.completion, cost: completionCost },
    recipeDetails: { model: models.details, cost: recipeDetailsCost, perRecipe: singleDetailCost },
    images:        { provider: 'Runware', cost: imagesCost, perImage: RUNWARE_PRICING.costPerImage },
    total:         parseFloat(total.toFixed(6)),
  };
};

//------------------------------------------------
// API HELPER FUNCTIONS
//------------------------------------------------

/**
 * Function to clean and extract JSON from responses that might contain markdown code blocks
 * @param {string} response - The response text to clean
 * @returns {string} Cleaned JSON response
 */
export const cleanJsonResponse = (response) => {
  if (!response || response.trim() === '') {
    return '';
  } 
  
  let cleanedResponse = response.trim();
  
  // Remove markdown code block markers
  if (cleanedResponse.startsWith('```json')) {
    cleanedResponse = cleanedResponse.substring(7);
  } else if (cleanedResponse.startsWith('```')) {
    cleanedResponse = cleanedResponse.substring(3);
  }
  
  if (cleanedResponse.endsWith('```')) {
    cleanedResponse = cleanedResponse.substring(0, cleanedResponse.length - 3);
  }
  
  // Try to extract JSON from anywhere in the response
  const jsonStartIndex = cleanedResponse.indexOf('{');
  const jsonEndIndex = cleanedResponse.lastIndexOf('}');
  
  if (jsonStartIndex !== -1 && jsonEndIndex !== -1 && jsonEndIndex > jsonStartIndex) {
    cleanedResponse = cleanedResponse.substring(jsonStartIndex, jsonEndIndex + 1);
  }
  
  return cleanedResponse.trim();
};